import { HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyColorsService } from '@bwc/src/app/services/company-colors.service';
import { VacaniesService } from '@bwc/src/app/services/vacanies.service';
import { AuthApiService, CompanyApiService, MeApiService } from '@digilize/shared/data/services/src';
import { ChangeProfileAndAddressModel, SkillType, SnackbarType } from '@digilize/shared/definitions/src';
import { DialogConfirmComponent } from '@digilize/shared/feature/components/src';
import { AuthService } from '@digilize/shared/feature/modules/auth/src';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { DialogService } from '@digilize/shared/utils/services/src';
import { dateBeforeNow, minArrayLengthValidator } from '@digilize/shared/utils/validators/src';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  debounceTime,
  delay,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import { ManagePasswordComponent } from '../../../components/manage-password/manage-password.component';
import { VacancyDetails } from '../../../components/vacancy-apply/vacancy-apply.component';
import { EducationLevels } from '../../../enums/enums';
import { enumToArrOfObjs } from '../../../helpers/helpers';
import { CvFormHelperService } from '../../../services/cv-form-helper.service';
import { CvService } from '../../../services/cv.service';
import { EventEmitterService } from '../../../services/event-emitter.service';
import { MeProfileService } from '../../../services/me-profile.service';
import { ProfileChangesService } from '../../../services/profile-changes.service';
import { SharedAccountDataService } from '../../../services/shared-account-data.service';
import { SkillsService } from '../../../services/skills.service';

@Component({
  selector: 'bwc-profile-check',
  templateUrl: './profile-check.component.html',
  styleUrls: ['./profile-check.component.scss'],
})
export class ProfileCheckComponent implements OnInit, OnDestroy {
  MAX_SOFTSKILLS = 6;
  MAX_KNOWLEDGE = 12;
  SORT_THRESHOLDS: Record<string, number> = {
    soft_skills: 6,
    knowledge_skills: 12,
    languages: 10,
  };
  @Output() popupWindow = new EventEmitter();

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault();
    }
  }

  form: UntypedFormGroup;
  levelOptions = enumToArrOfObjs(EducationLevels);

  activeEditPanel: string | null = null;
  expandedSections: Record<string, boolean> = {
    soft_skills: true,
    knowledge_skills: true,
    work_history: true,
    education: true,
    languages: true,
  };
  inlineEditingSections: Record<string, boolean> = {
    soft_skills: false,
    knowledge_skills: false,
    languages: false,
  };
  reorderingSections: Record<string, boolean> = {
    soft_skills: false,
    knowledge_skills: false,
    languages: false,
  };
  activeExcludeSkillType: SkillType[] = [];
  activeSectionTitle = '';
  isLoggedIn = false;
  isActivated = null;
  alreadyApplied = false;
  accountDataChecked = false;
  password_created = true;
  showApplyCont = false;
  showApplyOFLCont = false;
  dataLoaded = false;
  checkingIfApply = false;
  cvSharing = false;
  isSaving = false;
  hasUnsavedChanges = false;
  showSavedIndicator = false;
  private savedIndicatorTimeout: ReturnType<typeof setTimeout>;
  private autoSaveSuppressedUntil = 0;
  private saveComplete$ = new Subject<boolean>();
  showShareModal = false;
  vacancyDetails: VacancyDetails;
  currentDomain: string;
  userId: string;
  SkillType = SkillType;
  private openFormLinkId: string;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private meApiService: MeApiService,
    private authApiService: AuthApiService,
    private vacaniesService: VacaniesService,
    private cvService: CvService,
    private cvFormHelper: CvFormHelperService,
    private authService: AuthService,
    private fb: FormBuilder,
    private _eventEmitter: EventEmitterService,
    private dialogService: DialogService,
    private skillsService: SkillsService,
    private sharedAccountDataService: SharedAccountDataService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private companyApiServices: CompanyApiService,
    private companyColorsService: CompanyColorsService,
    private snackbarService: SnackbarService,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private profileChangesService: ProfileChangesService,
    private meProfileService: MeProfileService
  ) {
    this.form = new UntypedFormGroup({
      first_name: new UntypedFormControl('', Validators.required),
      last_name: new UntypedFormControl('', Validators.required),
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
      phone: new UntypedFormControl(''),
      photo: new UntypedFormControl(''),
      photo_id: new UntypedFormControl(''),
      city: new UntypedFormControl('', Validators.required),
      country: new UntypedFormControl('', Validators.required),
      birthday: new UntypedFormControl('', [Validators.required, dateBeforeNow()]),
      gender: new UntypedFormControl(''),
      soft_skills: new UntypedFormArray([]),
      knowledge_skills: new UntypedFormArray([]),
      task_skills: new UntypedFormArray([]),
      professional_skills: new UntypedFormArray([]),
      it_skills: new UntypedFormArray([]),
      skills: new UntypedFormArray([]),
      work_history: new UntypedFormArray([], minArrayLengthValidator(1)),
      education: new UntypedFormArray([]),
      languages: new UntypedFormArray([]),
    });
  }

  get showEditCont(): boolean {
    return this.activeEditPanel !== null;
  }

  ngOnInit(): void {
    this.checkParams();
    this._eventEmitter.dataStr.pipe(takeUntil(this.destroy$)).subscribe((msg) => {
      if (msg === 'saveProfile') {
        this.saveProfile();
      }
    });
    this.profileChangesService.hasChanges$.pipe(takeUntil(this.destroy$)).subscribe((hasChanges) => {
      this.hasUnsavedChanges = hasChanges;
    });
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.dataLoaded) {
        this.profileChangesService.markFormDirty();
      }
    });
    this.form
      .get('photo_id')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.dataLoaded) {
          this.profileChangesService.markPhotoChanged();
        }
      });
    this.form
      .get('photo')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((photo) => {
        if (this.dataLoaded) {
          this.sharedAccountDataService.updateCv('photo', photo);
        }
      });
    this.profileChangesService.saveRequested$
      .pipe(
        switchMap(() => this.saveChanges$()),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.profileChangesService.saveCompleted$.next();
      });

    // Auto-save with 600ms debounce — subscribe independently so
    // in-flight HTTP requests are not cancelled on component destroy
    this.profileChangesService.hasChanges$
      .pipe(
        filter((hasChanges) => hasChanges),
        debounceTime(600),
        filter(() => this.dataLoaded && !this.cvSharing && !this.isSaving && Date.now() > this.autoSaveSuppressedUntil),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.saveChanges$().subscribe((saved) => {
          if (saved) this.flashSavedIndicator();
        });
      });
  }

  ngOnDestroy(): void {
    this._eventEmitter.setClass(false);
    clearTimeout(this.savedIndicatorTimeout);
    this.destroy$.next();
    this.destroy$.complete();
  }

  discardChanges(): void {
    this.profileChangesService.markAsSaved();
  }

  // --- Edit panel management ---

  editPersonalData(): void {
    this.openEditPanel('gen_info');
  }

  editSkills(name: string): void {
    this.openEditPanel(name);
    this.popupWindow.emit(true);
  }

  editSkillsFiltered(section: string): void {
    if (section === 'soft_skills') {
      this.activeExcludeSkillType = [SkillType.Skills, SkillType.Knowledge, SkillType.Language];
      this.activeSectionTitle = this.translateService.instant('global.soft_skills');
    } else if (section === 'knowledge_skills') {
      this.activeExcludeSkillType = [SkillType.Softskill, SkillType.Language];
      this.activeSectionTitle = this.translateService.instant('global.knowledge_and_skills');
    }

    this.editSkills('search_add_skills');
  }

  closeEditCont(panelName?: string): void {
    const cancelled = panelName === 'cancelled';
    const closedPanel = cancelled ? this.activeEditPanel : panelName ?? this.activeEditPanel;
    this.activeEditPanel = null;
    this._eventEmitter.setClass(false);
    if (!cancelled && closedPanel && this.isRefreshableSection(closedPanel)) {
      if (this.hasUnsavedChanges) {
        this.saveNow().subscribe((saved) => {
          if (saved) {
            this.refreshSectionData(closedPanel);
          }
        });
      } else {
        this.refreshSectionData(closedPanel);
      }
    }
  }

  isEditPanelActive(name: string): boolean {
    return this.activeEditPanel === name;
  }

  // --- Skill rating ---

  changeSkillRating(req: any): void {
    this.profileChangesService.addRatingChange(req);
  }

  // --- Inline edit mode ---

  toggleInlineEditing(section: string): void {
    const enabling = !this.inlineEditingSections[section];
    this.inlineEditingSections[section] = enabling;
    if (enabling) {
      this.expandedSections[section] = true;
      this.reorderingSections[section] = true;
    } else {
      this.reorderingSections[section] = false;
    }
  }

  isInlineEditing(section: string): boolean {
    return this.inlineEditingSections[section] ?? false;
  }

  deleteSkillInline(section: string, index: number): void {
    const formArray = this.form.get(section) as FormArray;
    const skill = formArray.at(index);
    const skillId = skill?.value?.id;

    formArray.removeAt(index);
    if (skillId) {
      this.profileChangesService.addPendingSkillDeletion(skillId);
    }
    this.profileChangesService.markFormDirty();
  }

  canSort(section: string): boolean {
    const formArray = this.form.get(section) as FormArray;
    const threshold = this.SORT_THRESHOLDS[section] ?? 10;
    return formArray && formArray.length >= threshold;
  }

  onSortSkills(section: string): void {
    if (!this.canSort(section)) return;

    this.reorderingSections[section] = !this.reorderingSections[section];

    if (this.reorderingSections[section]) {
      this.expandedSections[section] = true;
    }
  }

  isReordering(section: string): boolean {
    return this.reorderingSections[section] ?? false;
  }

  onSkillsReordered(section: string): void {
    this.profileChangesService.markSectionReordered(section);
  }

  // --- Edit result handlers ---

  getEditedForm(event: FormGroup): void {
    this.form.patchValue(event.value);
  }

  getEditedJob(event: FormArray): void {
    this.work_history = event;
  }

  getEditedEducation(event: FormGroup): void {
    this.education = event.get('formSections') as FormArray;
  }

  getEditedLanguages(event: FormGroup): void {
    this.languages = event.get('selectedLanguages') as FormArray;
  }

  getEditedSkills(event: any, name: string): void {
    const skillsType = (this as any)[name] as FormArray;
    skillsType.clear();
    event.value.forEach((data) => {
      skillsType.push(
        this.fb.group({
          id: data.id,
          skill_id: data.skill_id,
          skill_name: data.skill_name,
          rating: data.rating,
        })
      );
    });
  }

  // --- Profile actions ---

  private saveProfile(): void {
    this.openEditPanel('create_account');
  }

  public onCreateAccount() {
    sessionStorage.setItem('redirectAfterActivation', 'vacancies');

    this.saveProfile();
  }

  shareCv(): void {
    this.openShareModal();
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this._eventEmitter.setClass(false);
  }

  applyForTheJob(): void {
    this.vacaniesService.vacancyApply(this.vacancyDetails.id, {}).subscribe({
      next: () => {
        this.removeParameterFromURL();
        this.showApplyCont = false;
        this.dialogService.openDialog(DialogConfirmComponent, {
          disableClose: false,
          width: '670px',
          data: {
            confirmBtn: this.translateService.instant('global.continue'),
            descriptionHtml: this.translateService.instant('global.thank_you_for_applying_to_the_vacancy'),
          },
        });
      },
      error: (err) => console.error('Error during applying for the vacancy', err),
    });
  }

  saveNow(): Observable<boolean> {
    if (this.isSaving) {
      return this.saveComplete$.pipe(take(1));
    }
    return this.saveChanges$().pipe(
      tap((saved) => {
        if (saved) this.flashSavedIndicator();
      })
    );
  }

  saveChanges$(): Observable<boolean> {
    if (this.isSaving) return of(false);
    this.isSaving = true;

    const pendingRatings = this.profileChangesService.pendingRatingChanges;
    const ratingCalls = pendingRatings.map((skill) => this.skillsService.postSkillsRank(skill));
    const reorderCalls = this.buildReorderCalls();
    const deleteCalls = this.profileChangesService.pendingSkillDeletions.map((id) =>
      this.skillsService.deleteUserSkill(id)
    );

    return this.sharedAccountDataService.cv$.pipe(
      take(1),
      switchMap((currentCv) => {
        const pendingPrefs = this.profileChangesService.pendingVacancyPreferences;
        const pendingVisibility = this.profileChangesService.pendingVisibility;

        const prefsCalls: Observable<unknown>[] = pendingPrefs
          ? [this.meApiService.updatePreferences(pendingPrefs)]
          : [];

        const visibilityCalls: Observable<unknown>[] =
          pendingVisibility !== null
            ? [this.meApiService.updateVisibility({ visible_to_employers: pendingVisibility })]
            : [];

        const profileCalls: Observable<unknown>[] = this.profileChangesService.pendingPhotoUpdate
          ? [this.meApiService.updatePhoto(this.form.value.photo_id)]
          : [];

        const allCalls = [
          ...prefsCalls,
          ...visibilityCalls,
          ...profileCalls,
          ...ratingCalls,
          ...reorderCalls,
          ...deleteCalls,
        ];
        const result$ = allCalls.length ? forkJoin(allCalls) : of([]);
        return result$.pipe(map(([cvResponse]) => cvResponse));
      }),
      finalize(() => (this.isSaving = false)),
      delay(300),
      switchMap(() => {
        this.profileChangesService.markAsSaved();
        return this.sharedAccountDataService.loadCv().pipe(map(() => true));
      }),
      catchError((err) => {
        console.error('Error saving changes:', err);
        this.autoSaveSuppressedUntil = Date.now() + 5000;
        this.snackbarService.openSnackbar(SnackbarType.Error, {
          duration: 5000,
          data: { msg: 'alert.custom.request_error', translate: true },
        });
        return of(false);
      }),
      tap((result) => this.saveComplete$.next(result))
    );
  }

  private buildProfilePayload(): ChangeProfileAndAddressModel {
    const v = this.form.value;
    return {
      email: v.email ?? null,
      phone: v.phone ?? null,
      first_name: v.first_name ?? null,
      last_name: v.last_name ?? null,
      nickname: null,
      description: null,
      gender: v.gender ?? null,
      birthday: v.birthday ?? null,
      post_code: null,
      city: v.city ?? null,
      country: v.country ?? null,
      street: null,
      house_number: null,
      street_number: null,
      photo_id: v.photo_id ?? null,
    };
  }

  private buildReorderCalls(): Observable<any>[] {
    const calls: Observable<any>[] = [];

    for (const section of this.profileChangesService.reorderedSections) {
      if (section === 'knowledge_skills') {
        const formArray = this.form.get(section) as FormArray;
        const allItems = formArray.value.map((skill, index) => ({ ...skill, order: index }));
        const knowledgeItems = allItems.filter((s) => s.type === SkillType.Knowledge || !s.type);
        const skillItems = allItems.filter((s) => s.type === SkillType.Skills);
        if (knowledgeItems.length) calls.push(this.skillsService.putKnowledgeSkills({ items: knowledgeItems }));
        if (skillItems.length) calls.push(this.skillsService.putSkills({ items: skillItems }));
      } else if (section === 'soft_skills') {
        const formArray = this.form.get(section) as FormArray;
        const items = formArray.value.map((skill, index) => ({ ...skill, order: index }));
        calls.push(this.skillsService.putSoftSkills({ items }));
      } else if (section === 'languages') {
        const formArray = this.form.get(section) as FormArray;
        const items = formArray.value.map((skill, index) => ({ ...skill, order: index }));
        calls.push(this.skillsService.putLanguagesSkills({ items }));
      }
    }

    return calls;
  }

  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] ?? true;
  }

  openPrint(): void {
    window.print();
  }

  // --- Form array accessors ---

  get softSkills(): FormArray {
    return this.form.get('soft_skills') as FormArray;
  }

  get knowledgeSkills(): FormArray {
    return this.form.get('knowledge_skills') as FormArray;
  }

  get profSkills(): FormArray {
    return this.form.get('professional_skills') as FormArray;
  }

  get taskSkills(): FormArray {
    return this.form.get('task_skills') as FormArray;
  }

  get itSkills(): FormArray {
    return this.form.get('it_skills') as FormArray;
  }

  get education(): FormArray {
    return this.form.get('education') as FormArray;
  }

  set education(value: FormArray) {
    this.form.setControl('education', value);
  }

  get languages(): FormArray {
    return this.form.get('languages') as FormArray;
  }

  set languages(value: FormArray) {
    this.form.setControl('languages', value);
  }

  get work_history(): FormArray {
    return this.form.get('work_history') as FormArray;
  }

  set work_history(value: FormArray) {
    this.form.setControl('work_history', value);
  }

  // --- Private helpers ---

  private openShareModal(): void {
    this._eventEmitter.setClass(true);
    this.showShareModal = true;
  }

  private reloadProfileData(): void {
    this.dataLoaded = false;
    this.clearAllFormArrays();
    forkJoin({
      getCV: this.sharedAccountDataService.loadCv(),
      getMe: this.meApiService.getMe(),
    }).subscribe({
      next: (res: any) => {
        this.patchUserValues(res.getMe, false);
        this.cvFormHelper.patchUserPhoto(this.form, res.getCV);
        this.cvFormHelper.populateFormArraysFromCv(res.getCV, this.getFormArrays(), true);
        this.dataLoaded = true;
      },
      error: (err) => console.error(err),
    });
  }

  private flashSavedIndicator(): void {
    clearTimeout(this.savedIndicatorTimeout);
    this.showSavedIndicator = true;
    this.savedIndicatorTimeout = setTimeout(() => {
      this.showSavedIndicator = false;
    }, 2000);
  }

  private openEditPanel(name: string): void {
    this._eventEmitter.setClass(true);
    if (name === 'create_account') {
      this._eventEmitter.sendMessage('createAccountPanelOpened');
    }
    if (this.hasUnsavedChanges || this.isSaving) {
      this.saveNow().subscribe(() => {
        this.activeEditPanel = name;
      });
    } else {
      this.activeEditPanel = name;
    }
  }

  private isRefreshableSection(name: string): boolean {
    return [
      'soft_skills',
      'knowledge_skills',
      'professional_skills',
      'task_skills',
      'it_skills',
      'search_add_skills',
    ].includes(name);
  }

  private refreshSectionData(sectionName: string): void {
    this.sharedAccountDataService.loadCv().subscribe((res) => {
      if (sectionName === 'search_add_skills') {
        this.clearAllFormArrays();
        const formArrays = this.getFormArrays();
        this.cvFormHelper.populateFormArraysFromCv(res, formArrays, true);
        return;
      }

      if (sectionName === 'your_experience') {
        this.work_history.clear();
        this.cvFormHelper.addWorkHistoryData(res, this.work_history, true);
        return;
      }

      if (sectionName === 'education') {
        this.education.clear();
        this.cvFormHelper.educationToFormArray(res, 'education', this.education);
        return;
      }

      const formArrays: Record<string, FormArray> = {
        soft_skills: this.softSkills,
        knowledge_skills: this.knowledgeSkills,
        professional_skills: this.profSkills,
        task_skills: this.taskSkills,
        it_skills: this.itSkills,
        languages: this.languages,
      };

      this.cvFormHelper.refreshSkillData(res, sectionName, formArrays[sectionName]);
    });
  }

  private clearAllFormArrays(): void {
    this.softSkills.clear();
    this.knowledgeSkills.clear();
    this.profSkills.clear();
    this.taskSkills.clear();
    this.itSkills.clear();
    this.education.clear();
    this.languages.clear();
    this.work_history.clear();
  }

  private checkParams(): void {
    this.route.queryParams.subscribe((params) => {
      const activateToken = params['activate_token'];
      const applyCompanyId = params['c'];
      this.openFormLinkId = params['ofl'];
      const showUserCvId = params['uid'];

      if (showUserCvId) {
        this.loadSharedCv(showUserCvId);
      } else {
        this.loadOwnProfile();
      }

      if (applyCompanyId) {
        this.loadCompanyDetails(applyCompanyId);
      }

      if (activateToken) {
        this.handleActivationToken(activateToken);
      }
    });
  }

  private loadSharedCv(userId: string): void {
    this.cvSharing = true;
    this.renderer.addClass(document.body, 'cv-sharing');

    this.cvService.getCVbyId(userId).subscribe({
      next: (data) => {
        this.patchUserValues(data, true);
        this.cvFormHelper.patchUserPhoto(this.form, data);
        this.cvFormHelper.populateFormArraysFromCv(data, this.getFormArrays(), true);
      },
      error: (err) => {
        console.error(err);
        this.authService.logout();
      },
    });
  }

  private loadOwnProfile(): void {
    this.cvSharing = false;
    this.isLoggedIn = !!this.authService.isLoggedIn();
    this.renderer.addClass(document.body, 'profile-check');
    this.currentDomain = window.location.origin;
    this.listenToSharedAccountData();

    forkJoin({
      getCV: this.sharedAccountDataService.cv$.pipe(take(1)),
      getMe: this.meApiService.getMe(),
    }).subscribe({
      next: (res) => {
        this.userId = res.getCV.id;
        this.patchUserValues(res.getMe, false);
        // this.shareData(res.getMe, res.getCV.photo?.url1x);
        this.cvFormHelper.patchUserPhoto(this.form, res.getCV);
        this.cvFormHelper.populateFormArraysFromCv(res.getCV, this.getFormArrays(), true);
        this.dataLoaded = true;
      },
      error: (err) => console.error(err),
    });
  }

  private loadCompanyDetails(companyId: string): void {
    this.companyApiServices.getCompanyStyles(companyId).subscribe({
      next: (data: any) => this.companyColorsService.setCompanyColors(data.colors_model),
      error: (err) => console.error(err),
    });
  }

  private handleActivationToken(activateToken: string): void {
    const token = this.authService.getAccessToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.removeQueryParam('activate_token');

    this.authApiService.activateProfileWithToken({ activate_token: activateToken }, headers).subscribe({
      next: () => {
        this.isActivated = true;
        const dialogRef = this.dialogService.openDialog(ManagePasswordComponent, {
          disableClose: false,
          width: '670px',
          data: { account_activated: true, email: this.form.value.email },
        });
        this.removeQueryParam('activate_token');
        this.redirectAfterActivationIfNeeded(dialogRef);
      },
      error: (err) => {
        console.error('Error during activateProfileWithToken', err);
        this.dialogService.openDialog(ManagePasswordComponent, {
          disableClose: false,
          width: '670px',
          data: { activation_error: true, email: this.form.value.email },
        });
      },
    });
  }

  private listenToSharedAccountData(): void {
    this.sharedAccountDataService.cv$.subscribe({
      next: (cv) => {
        const wasActivated = this.isActivated;
        this.isActivated = !!cv?.activated;
        this.password_created = cv?.password_created;
        if (!wasActivated && wasActivated !== null && this.isActivated) {
          this.saveChanges$().subscribe(() => {
            this.redirectAfterActivationIfNeeded();
          });
        }

        if (this.openFormLinkId && this.isActivated) {
          setTimeout(() => {
            this.dialog.closeAll();
            this.joinOpenFormLink(this.openFormLinkId, cv.email);
          }, 1500);
        }

        if (this.openFormLinkId && this.form.value.email && !this.isActivated) {
          this.checkForOpenApply(this.form.value.email);
        }
      },
      error: (err) => console.error(err),
    });
  }

  private joinOpenFormLink(openId: string, email: string): void {
    const cookieName = 'ofl-' + openId;
    const openFormCookie = this.cookieService.get(cookieName);

    if (openFormCookie !== email) {
      this.cookieService.set(cookieName, email, { expires: 7, path: '/', sameSite: 'Strict', secure: true });

      this.companyApiServices
        .checkOpenFormLink(openId)
        .pipe(
          catchError((err) => {
            this.snackbarService.openSnackbar(SnackbarType.Error, {
              duration: 5000,
              data: { msg: 'alert.custom.request_error', translate: true },
            });
            return throwError(err);
          })
        )
        .subscribe({
          next: () => {
            const title$ = this.translateService.get('global.you_just_applied');
            const text$ = this.translateService.get('global.ofl_info');

            forkJoin([title$, text$]).subscribe(([title, text]) => {
              const html = "<div class='popup-info'>" + title + '<span>' + text + '</span></div>';
              const safeHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
              this.showApplyOFLCont = false;

              this.dialogService.openDialog(DialogConfirmComponent, {
                disableClose: false,
                width: '750px',
                backdropClass: 'openformlink-backdrop',
                panelClass: 'openformlink-dialog-container',
                data: {
                  confirmBtn: this.translateService.instant('global.continue'),
                  descriptionHtml: safeHtml,
                },
              });
            });
          },
          error: (err) => console.error(err),
        });
    }
  }

  // private shareData(data, photoUrl?: string): void {
  //   this.sharedAccountDataService.sendData({
  //     activated: data.activated,
  //     password_created: data.password_created,
  //     email: data.email,
  //     photoUrl,
  //   });
  // }

  private checkForOpenApply(email: string): void {
    const cookieName = 'ofl-' + this.openFormLinkId;
    if (this.cookieService.get(cookieName) !== email) {
      this.showApplyOFLCont = true;
    }
  }

  private patchUserValues(data, useBirthDate: boolean): void {
    this.isActivated = !!data.activated;
    this.password_created = data.password_created;
    this.accountDataChecked = true;
    this.form.patchValue({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone,
      city: data.city,
      country: data.country,
      birthday: useBirthDate ? data.birth_date : data.birthday,
      gender: data.gender,
    });
  }

  private removeParameterFromURL(): void {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    currentUrlTree.queryParams = {};
    this.router.navigateByUrl(currentUrlTree);
  }

  private removeQueryParam(param: string): void {
    const snapshot = this.route.snapshot;
    const params = { ...snapshot.queryParams };
    delete params[param];
    this.router.navigate([], { queryParams: params });
  }

  private getFormArrays() {
    return {
      softSkills: this.softSkills,
      knowledgeSkills: this.knowledgeSkills,
      profSkills: this.profSkills,
      taskSkills: this.taskSkills,
      itSkills: this.itSkills,
      education: this.education,
      languages: this.languages,
      workHistory: this.work_history,
    };
  }

  private redirectAfterActivationIfNeeded(dialogRef?: { afterClosed: () => Observable<any> }): void {
    const redirectTo = sessionStorage.getItem('redirectAfterActivation');
    if (redirectTo !== 'vacancies') return;

    sessionStorage.removeItem('redirectAfterActivation');
    if (dialogRef) {
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          this.router.navigate(['profile-check', 'vacancies']);
        });
    } else {
      this.router.navigate(['profile-check', 'vacancies']);
    }
  }
}
